import { type GlyphKey } from '@kk/domain';
import { Icon, SectionLabel, Text, TextField, Title2, WasabiButton } from '@kk/ui';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { useContentCan, useSuperAdmin } from '@/auth/SessionProvider';
import { LinkButton, Status } from '@/components/bits';
import { GlyphPicker } from '@/components/GlyphPicker';
import { deck } from '@/copy/deck';
import { placeholders } from '@/copy/placeholders';
import {
  useAdventureVersions,
  useCreateMission,
  useDeleteMission,
  useDiscardAdventureDraft,
  usePublishAdventure,
  useRestoreAdventureVersion,
  useErrorMessage,
  useQuestContent,
  useReorderMissions,
  useSyncQuestSource,
  useUpdateAdventure,
} from '@/data/hooks';
import { relativeTime } from '@/lib/time';
import type { AdminAdventure, AdminMission } from '@/types/wire';

const c = deck.o11;

/** Was this mission ever published? Only one that never was can be deleted; the rest are retired. */
const everPublished = (m: AdminMission) =>
  m.status === 'live' || m.status === 'retired' || m.liveVersion !== undefined || !!m.publishedAt;

/**
 * **"What you'll learn"** (C12). New words wait as a draft — off the trail — until someone approves them, because an
 * approval is for the words it was given. Approve is offered only for what is saved.
 */
function LearnPanel({ adventure, run }: { adventure: AdminAdventure; run: (fn: () => Promise<unknown>) => void }) {
  const update = useUpdateAdventure();
  const [text, setText] = useState(adventure.learn?.text ?? '');
  const saved = adventure.learn?.text ?? '';
  const dirty = text.trim() !== saved;
  const approved = adventure.learn?.status === 'approved';
  return (
    <section className="stack" data-testid="learn">
      <div className="script-head">
        <SectionLabel>{c.learn}</SectionLabel>
        {saved ? (
          <Text variant="bodySm" tone={approved ? 'ink' : 'muted'} testID="learn-state">
            {/* approved words in an unpublished draft are not on the trail yet: the published line still is */}
            {approved ? (adventure.hasDraft ? c.learnApprovedPending : c.learnApproved) : c.learnDraft}
          </Text>
        ) : null}
      </div>
      <textarea
        className="script-text"
        aria-label={c.learn}
        value={text}
        onChange={(e) => setText(e.target.value)}
        data-testid="learn-text"
      />
      <div className="actions">
        {dirty ? (
          <WasabiButton
            label={placeholders.settings.save}
            size="sm"
            fullWidth={false}
            onPress={() =>
              run(() => update.mutateAsync({ id: adventure.id, learn: text.trim() ? { text } : null }))
            }
            testID="learn-save"
          />
        ) : saved ? (
          <LinkButton
            label={approved ? c.withdraw : c.approve}
            onPress={() => run(() => update.mutateAsync({ id: adventure.id, learn: { approved: !approved } }))}
            testID="learn-approve"
          />
        ) : null}
      </div>
    </section>
  );
}

/** The adventure's badge: its name and its picture (a key from the registry; an unknown one is refused). */
function BadgePanel({ adventure, run }: { adventure: AdminAdventure; run: (fn: () => Promise<unknown>) => void }) {
  const update = useUpdateAdventure();
  const [name, setName] = useState(adventure.badge?.name ?? '');
  const [art, setArt] = useState<string | undefined>(adventure.badge?.art);
  const [picking, setPicking] = useState(false);
  const dirty = name !== (adventure.badge?.name ?? '') || art !== adventure.badge?.art;
  return (
    <section className="stack" data-testid="badge">
      <SectionLabel>{c.badge}</SectionLabel>
      <div className="tile-row">
        <div className="tile-preview" aria-hidden>
          {art ? <Icon name={art as GlyphKey} size={24} /> : <span className="tile-type">Aa</span>}
        </div>
        <TextField label={c.badgeName} value={name} onChangeText={setName} testID="badge-name" />
        <LinkButton label={c.picture} onPress={() => setPicking((v) => !v)} testID="badge-pick" />
      </div>
      {picking ? (
        <GlyphPicker
          value={art}
          testID="badge-picker"
          onChange={(key) => {
            setArt(key);
            setPicking(false);
          }}
        />
      ) : null}
      {dirty ? (
        <div className="actions">
          <WasabiButton
            label={placeholders.settings.save}
            size="sm"
            fullWidth={false}
            onPress={() =>
              run(() =>
                update.mutateAsync({
                  id: adventure.id,
                  badge: { ...(adventure.badge?.id ? { id: adventure.badge.id } : {}), name, ...(art ? { art } : {}) },
                }),
              )
            }
            testID="badge-save"
          />
        </div>
      ) : null}
    </section>
  );
}

/** The adventure's missions: open one, put them in order, delete one nobody ever saw, or start a new one. */
function MissionsPanel({ adventure, run }: { adventure: AdminAdventure; run: (fn: () => Promise<unknown>) => void }) {
  const navigate = useNavigate();
  const reorder = useReorderMissions();
  const remove = useDeleteMission();
  const mayEdit = useContentCan('edit');
  const create = useCreateMission();
  const [title, setTitle] = useState('');
  const ids = adventure.missions.map((m) => m.id);
  const moveTo = (from: number, to: number) => {
    const next = [...ids];
    const [id] = next.splice(from, 1);
    next.splice(to, 0, id!);
    run(() => reorder.mutateAsync({ adventureId: adventure.id, missionIds: next }));
  };
  return (
    <section className="stack" data-testid="missions">
      <SectionLabel>{c.missions}</SectionLabel>
      {adventure.missions.map((m, i) => (
        <div className="mission-row" key={m.id} data-testid={`mission-${m.id}`}>
          <button
            type="button"
            className="tree-row"
            onClick={() => navigate(`/quest/${adventure.id}/${m.id}`)}
            data-testid={`open-${m.id}`}
          >
            <Text variant="bodySm" tone="ink">
              {deck.o06.missionEyebrow(m.order, m.title)}
            </Text>
            <Text variant="bodySm" tone="muted">
              {deck.o06.status(m.status)}
            </Text>
          </button>
          <div className="arrange">
            {i > 0 ? (
              <button type="button" className="link" onClick={() => moveTo(i, i - 1)} data-testid={`up-${m.id}`}>
                {c.moveUp}
              </button>
            ) : null}
            {i < ids.length - 1 ? (
              <button type="button" className="link" onClick={() => moveTo(i, i + 1)} data-testid={`down-${m.id}`}>
                {c.moveDown}
              </button>
            ) : null}
            {/* a published mission is retired, never deleted: a child's finished run must still open */}
            {everPublished(m) ? null : (
              <button
                type="button"
                className="link muted"
                onClick={() => run(() => remove.mutateAsync(m.id))}
                data-testid={`delete-${m.id}`}
              >
                {c.deleteMission}
              </button>
            )}
          </div>
        </div>
      ))}
      <Text variant="bodySm" tone="muted">
        {c.deleteHint}
      </Text>
      {/* its own row, not the quiz tiles' 44px grid, which squeezed the field and wrapped its label a few letters a line */}
      {mayEdit ? (
        <div className="new-mission-row">
          <TextField label={c.missionTitle} value={title} onChangeText={setTitle} testID="new-mission-title" />
          <WasabiButton
            label={deck.o06.newMission}
            size="sm"
            fullWidth={false}
            disabled={!title.trim()}
            onPress={() =>
              run(async () => {
                const made = await create.mutateAsync({ adventureId: adventure.id, title, concept: title });
                setTitle('');
                navigate(`/quest/${adventure.id}/${made.id}`);
              })
            }
            testID="new-mission"
          />
        </div>
      ) : null}
    </section>
  );
}

/**
 * **An adventure is published like a mission** (Evan, 2026-09-26: "nothing an author does reaches children without a
 * publish"). Its words, badge, "What you'll learn" and mission order wait in a draft; this is where it goes live, is
 * thrown away, or an old version is restored.
 */
function AdventureDraftBar({ adventure, run }: { adventure: AdminAdventure; run: (fn: () => Promise<unknown>) => void }) {
  const publish = usePublishAdventure();
  const discard = useDiscardAdventureDraft();
  const mayPublish = useContentCan('publish');
  const mayEdit = useContentCan('edit');
  if (!adventure.hasDraft) return null;
  return (
    <div className="draft-banner stack" data-testid="adventure-draft">
      <Text variant="bodySm">{c.adventureDraft}</Text>
      <div className="actions">
        {mayPublish ? (
          <WasabiButton
            label={deck.o06.publish}
            size="sm"
            fullWidth={false}
            onPress={() => run(() => publish.mutateAsync(adventure.id))}
            testID="adventure-publish"
          />
        ) : null}
        {mayEdit ? (
          <LinkButton
            label={c.discardDraft}
            onPress={() => run(() => discard.mutateAsync(adventure.id))}
            testID="adventure-discard"
          />
        ) : null}
      </div>
    </div>
  );
}

function AdventureHistory({ adventure, run }: { adventure: AdminAdventure; run: (fn: () => Promise<unknown>) => void }) {
  const history = useAdventureVersions(adventure.id);
  const restore = useRestoreAdventureVersion();
  const versions = history.data?.versions ?? [];
  if (versions.length === 0) return null;
  return (
    <section className="stack" data-testid="adventure-history">
      <SectionLabel>{deck.o13.history}</SectionLabel>
      {versions.map((v) => {
        const live = v.version === history.data?.liveVersion;
        return (
          <div className={`version-card${live ? ' live' : ''}`} key={v.version} data-testid={`adventure-version-${v.version}`}>
            <div className="stack">
              <Text variant="labelStrong">{deck.o13.version(v.version, live)}</Text>
              <Text variant="bodySm" tone="muted">
                {deck.o13.publishedBy(v.publishedBy, relativeTime(v.publishedAt))}
              </Text>
              {v.note ? (
                <Text variant="bodySm" tone="muted">
                  {v.note}
                </Text>
              ) : null}
            </div>
            {live ? null : (
              <LinkButton
                label={deck.o13.restore}
                onPress={() => run(() => restore.mutateAsync({ id: adventure.id, version: v.version }))}
                testID={`adventure-restore-${v.version}`}
              />
            )}
          </div>
        );
      })}
    </section>
  );
}

function AdventurePanel({ adventure }: { adventure: AdminAdventure }) {
  const errorMessage = useErrorMessage();
  const [error, setError] = useState<string | null>(null);
  const run = (fn: () => Promise<unknown>) => {
    setError(null);
    fn().catch((e: unknown) => setError(errorMessage(e)));
  };
  return (
    <div className="stack" data-testid={`adventure-panel-${adventure.id}`}>
      <Title2 tone="ink">{deck.o06.adventureEyebrow(adventure.order, adventure.title)}</Title2>
      <AdventureDraftBar adventure={adventure} run={run} />
      <LearnPanel key={`learn-${adventure.learn?.text ?? ''}`} adventure={adventure} run={run} />
      <BadgePanel key={`badge-${JSON.stringify(adventure.badge ?? {})}`} adventure={adventure} run={run} />
      <MissionsPanel adventure={adventure} run={run} />
      <AdventureHistory adventure={adventure} run={run} />
      {error ? <Status testID="adventure-error">{error}</Status> : null}
    </div>
  );
}

function AdventureRow({ a, open, onOpen }: { a: AdminAdventure; open: boolean; onOpen: (id: string) => void }) {
  return (
    <button
      type="button"
      className={`tree-row${open ? ' is-open' : ''}`}
      onClick={() => onOpen(a.id)}
      data-testid={`adventure-${a.id}`}
    >
      <Text variant="bodySm" tone="ink">
        {deck.o06.adventureEyebrow(a.order, a.title)}
      </Text>
      <Text variant="bodySm" tone="muted">
        {deck.o06.status(a.status)}
      </Text>
    </button>
  );
}

/**
 * **The Quest Jr. content library** (O06, and the content engine's O10 at admin.kiddiekredit.com/quest). Adventures
 * and their missions; an adventure's own fields — "What you'll learn", the badge, the order of its missions — are edited
 * here, and a mission opens in its editor (O11, O12) at `/quest/:adventureId/:missionId`.
 *
 * **The old whole-mission script boxes are gone** (content engine part 2): a child reads the beats and the questions,
 * which the mission editor edits line by line with their line counters.
 */
export function QuestContentScreen() {
  const errorMessage = useErrorMessage();
  const superAdmin = useSuperAdmin();
  const content = useQuestContent(superAdmin);
  const sync = useSyncQuestSource();
  const [openAdventure, setOpenAdventure] = useState<string | null>(null);
  const [showRetired, setShowRetired] = useState(false);

  const adventures = useMemo<AdminAdventure[]>(() => content.data ?? [], [content.data]);
  // retired adventures keep their numbers, so among the live ones they clash (two Adventure 1s): they sit apart, closed
  const current = adventures.filter((a) => a.status !== 'retired');
  const retired = adventures.filter((a) => a.status === 'retired');
  const adventure = adventures.find((a) => a.id === openAdventure) ?? current[0] ?? adventures[0];

  // read, never a probe: a 403 would be equally consistent with an expired token (M-41)
  if (!superAdmin) return <Status testID="admin-only">{deck.o06.adminOnly}</Status>;

  const status = content.isPending
    ? placeholders.empty.loading
    : content.error
      ? errorMessage(content.error)
      : adventures.length === 0
        ? deck.o06.empty
        : null;

  return (
    <>
      <div className="page-header">
        <div className="page-header-text">
          <Title2 tone="ink">{deck.o06.title}</Title2>
          <Text variant="bodySm" tone="muted">
            {deck.o06.subtitle}
          </Text>
        </div>
        <LinkButton
          label={deck.o06.sync}
          onPress={() => sync.mutateAsync().catch(() => undefined)}
          testID="sync-source"
        />
      </div>
      {sync.data ? (
        <Status testID="sync-result">{deck.o06.syncResult(sync.data.checked, sync.data.flagged.length)}</Status>
      ) : null}

      {status ? (
        <Status testID="quest-status">{status}</Status>
      ) : (
        <div className="quest-layout">
          <nav className="quest-tree" aria-label={deck.o06.adventures}>
            <SectionLabel>{deck.o06.adventures}</SectionLabel>
            {current.map((a) => (
              <AdventureRow key={a.id} a={a} open={a.id === adventure?.id} onOpen={setOpenAdventure} />
            ))}
            {retired.length ? (
              <>
                <button
                  type="button"
                  className="tree-group"
                  aria-expanded={showRetired}
                  onClick={() => setShowRetired((v) => !v)}
                  data-testid="retired-adventures"
                >
                  <SectionLabel>{deck.o06.status('retired')}</SectionLabel>
                </button>
                {showRetired
                  ? retired.map((a) => (
                      <AdventureRow key={a.id} a={a} open={a.id === adventure?.id} onOpen={setOpenAdventure} />
                    ))
                  : null}
              </>
            ) : null}
          </nav>
          <div className="quest-editor">
            {adventure ? <AdventurePanel key={adventure.id} adventure={adventure} /> : null}
          </div>
        </div>
      )}
    </>
  );
}
